import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './index.module.less';
import cx from 'classnames';
import wsuiApi from '@api/wsuiApi';
import type { ISceneItem } from '@typing/wsui/getRecommendScene';
import SearchInput from '@components/searchInput';
import { useChatStore } from '@store/chatStore';
import { useConfigStore } from '@store/configStore';
import { useUserStore } from '@store/userStore';
import { usePassport } from '@hooks/usePassport';
import { useUbcSend } from '@hooks/useUbcSend';
import { ConversationSceneType } from '@typing/wsui/msg';
import { ImageItem } from '@components/searchInput/useImageUpload';
import { IMessageImage } from '@typing/wsui/getMessages';
let iconArrow = 'https://muzhi-public-pic.cdn.bcebos.com/100115329441';

interface IFeatureCard {
    title: string;
    icon: string;
    questions: string[];
}

// 图标映射配置
const ICON_MAP: Record<string, string> = {
    search: 'https://muzhi-public-pic.cdn.bcebos.com/100115365805',
    line: 'https://muzhi-public-pic.cdn.bcebos.com/100115365809',
    doc: 'https://muzhi-public-pic.cdn.bcebos.com/100115365808',
};

const WelcomePage: React.FC = () => {
    const { ubcSend } = useUbcSend();
    const navigate = useNavigate();
    const { setPendingQuery } = useChatStore();
    const { setCoreSession, coreSessionId } = useChatStore();
    const { config, setConfig, setLoading: setConfigLoading, setError } = useConfigStore();
    const { isLogin } = useUserStore();
    const { showLogin } = usePassport();
    const [featureCards, setFeatureCards] = useState<IFeatureCard[]>([]);
    const [submitting, setSubmitting] = useState<boolean>(false);

    // 获取推荐场景数据
    useEffect(() => {
        const fetchRecommendScene = async () => {
            try {
                const response = await wsuiApi.getRecommendScene({businessType: 'doctor_youyi'});
                if (response.errno === 0 && response.data?.scenes) {
                    const cards = response.data.scenes.map((scene: ISceneItem) => ({
                        title: scene.title,
                        icon: ICON_MAP[scene.icon] || ICON_MAP['search'],
                        questions: scene.items.map((item) => item.content),
                    }));
                    setFeatureCards(cards);
                }
            } catch (error) {
                console.error('获取推荐场景失败:', error);
            } finally {
            }
        };

        fetchRecommendScene();
    }, []);

    /**
     * 初始化 coredata（获取 sessionId）+ otherdata（获取配置）
     * coredata 必须先于 otherdata 完成（otherdata 需要 sessionId）
     */
    const initCoreData = useCallback(async () => {
        // 已有 coreSessionId 则跳过
        if (coreSessionId) return;

        try {
            const coredataRes = await wsuiApi.getCoredata({
                ctrlData: {
                    newSession: 1,
                    tmpBotVersion2: 1,
                    showTab: 0,
                },
                businessType: 'doctor_youyi',
                appKey: 'pc_wenzhen',
            });

            if (coredataRes.errno === 0 && coredataRes.data?.session?.sessionId) {
                const sessionId = coredataRes.data.session.sessionId;
                setCoreSession(sessionId);

                // 并行获取 otherdata（配置，不影响主流程，对齐 vita mix 页面 useInitDataController:118-121）
                wsuiApi
                    .getOtherdata({
                        sessionId,
                        ctrlData: { showTab: 0 },
                        businessType: 'doctor_youyi',
                        appKey: 'pc_wenzhen',
                    })
                    .catch((err) => {
                        console.error('获取 otherdata 失败:', err);
                    });
            }
        } catch (error) {
            console.error('初始化 coredata 失败:', error);
        }
    }, [coreSessionId, setCoreSession]);

    /**
     * 获取配置数据（内部判断是否需要请求）
     */
    const getConfig = useCallback(async () => {
        if (config) return;

        setConfigLoading(true);
        try {
            const response = await wsuiApi.getConfig({businessType: 'doctor_youyi'});
            if (response.errno === 0 && response.data) {
                setConfig(response.data);
            }
        } catch (error) {
            console.error('获取配置失败:', error);
            setError(error as Error);
        } finally {
            setConfigLoading(false);
        }
    }, [config, setConfig, setConfigLoading, setError]);

    // 组件挂载时初始化（等登录态就绪后执行 coredata + getConfig）
    useEffect(() => {
        if (!isLogin) return;
        initCoreData();
        getConfig();
        ubcSend({
            type: 'view',
            value: 'page',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLogin]);

    useEffect(() => {
        const questions = featureCards?.map((card) => card?.questions.join('&'))?.join('&') || '';
        ubcSend({
            type: 'view',
            value: 'home_appendAsk',
            ext: {
                product_info: {
                    content: questions,
                },
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [featureCards]);

    /**
     * 实际执行提交逻辑
     * 使用 coreSessionId 导航到已知 session，无需等待 SSE 返回 session_id
     */
    const doSubmit = useCallback(
        async (value: string, sceneType: ConversationSceneType, images?: IMessageImage[]) => {
            try {
                ubcSend({
                    type: 'clk',
                    value: 'home_appendAsk',
                    ext: {
                        product_info: {
                            content: value.trim(),
                        },
                    },
                });

                ubcSend({
                    type: 'clk',
                    value: 'ask_msg',
                    ext: {
                        product_info: {
                            ask_type: 'recommend',
                            content: value.trim(),
                        },
                    },
                });

                setSubmitting(true);

                // 保存待发送的问题到 store
                setPendingQuery(value, sceneType, images);

                // 若 coredata 已完成，直接导航到已知 session；否则回退到无 session 路径
                const sessionId = useChatStore.getState().coreSessionId;
                if (sessionId) {
                    navigate(`/workbench/web/chat/${sessionId}`);
                } else {
                    navigate('/workbench/web/chat');
                }
            } catch (error) {
                console.error('提交失败:', error);
            } finally {
                setSubmitting(false);
            }
        },
        [ubcSend, setPendingQuery, navigate]
    );

    /**
     * 处理搜索提交（含登录态检查）
     */
    const handleSubmit = async (value: string, images?: ImageItem[]): Promise<void> => {
        if ((!value.trim() && (!images || images.length === 0)) || submitting) return;

        const msgImages = images
            ?.filter((i) => i.uploadStatus === 'success')
            .map((i) => ({
                fileName: i.fileName,
                icon: i.iconUrl || i.uploadedUrl || i.url,
                origin: i.uploadedUrl || i.url,
                small: i.smallUrl || i.uploadedUrl || i.url,
            }));

        if (!isLogin) {
            showLogin(async () => {
                await initCoreData();
                getConfig();
                doSubmit(value, ConversationSceneType.UserInput, msgImages);
            });
            return Promise.reject();
        }

        doSubmit(value, ConversationSceneType.UserInput, msgImages);
    };

    /**
     * 处理功能卡片问题点击（首页引导）
     */
    const handleCardQuestionClick = async (question: string): Promise<void> => {
        if (!question.trim() || submitting) return;

        if (!isLogin) {
            showLogin(() => {
                doSubmit(question, ConversationSceneType.ImWelcomeModuleHome);
            });
            return;
        }

        doSubmit(question, ConversationSceneType.ImWelcomeModuleHome);
    };

    return (
        <div className={styles.box}>
            <main className={styles.welcomePage}>
                {/* 欢迎区域 */}
                <section className={styles.welcomeSection}>
                    <div className={styles.welcomeText}>
                        <img
                            src="https://med-fe.cdn.bcebos.com/fe-doctor-workbench/pcoewelcome.png"
                            alt={config?.title || ''}
                            className={styles.title}
                            draggable={false}
                        />
                    </div>
                </section>

                <div className={styles.searchInput}>
                    {/* 搜索输入框 */}
                    <SearchInput
                        placeholder={config?.input_placeholder || ''}
                        onSubmit={handleSubmit}
                        minHeight={139}
                        maxHeight={327}
                    />
                </div>
                <div className={styles.inputBg}></div>

                {/* 功能卡片区域 */}
                <section className={styles.featuresSection}>
                    {featureCards.map((card) => (
                        <article key={card.title} className={styles.featureCard}>
                            <header className={styles.cardHeader}>
                                <div className={styles.iconWrapper}>
                                    <img src={card.icon} alt="" className={styles.icon} />
                                </div>
                                <h2 className={styles.cardTitle}>{card.title}</h2>
                            </header>
                            <ul className={styles.questionList}>
                                {card.questions.map((question, index) => (
                                    <li
                                        key={index}
                                        className={styles.questionItem}
                                        onClick={() => handleCardQuestionClick(question)}
                                    >
                                        <span className={cx(styles.questionText, 'text-clamp-2')}>{question}</span>
                                        <div className={styles.questionArrowContent}>
                                            <div className={styles.questionArrow}>
                                                <img src={iconArrow} alt="" />
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </article>
                    ))}
                </section>

                {/* 底部免责声明 */}
                <footer className={styles.disclaimer}>
                    <img src="https://muzhi-public-pic.cdn.bcebos.com/100115337386" alt=""></img>
                </footer>
            </main>
        </div>
    );
};

export default WelcomePage;
